(ns app.front.view.model-codes.common
  (:require [app.domain.model-codes.model :as model-codes-model]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.select :as ui-select]
            [app.front.util :as futil]
            [clojure.string :as str]
            [core.front.event :refer [<sub >evt]]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(def default-books-local-storage-key "default-books")
(def recent-codes-local-storage-key "recent-codes")

(rf/reg-event-fx ::init
  (fn [{:keys [db]} [_ group-id select-default-book?]]
    (let [default-book-id (when select-default-book?
                            (-> (futil/local-storage-get-json default-books-local-storage-key)
                                (get (str group-id))))]
      {:db       (assoc db ::data {::books-loading? true})
       :dispatch [::fetch-books group-id default-book-id]})))

(rf/reg-event-fx ::fetch-books
  (fn [_ [_ group-id default-book-id]]
    {:comm/command {:command    :model-codes/group-selected-books
                    :method     :get
                    :data       {:group-id group-id}
                    :on-success [::fetch-books.success group-id default-book-id]}}))

(rf/reg-event-fx ::fetch-books.success
  (fn [{:keys [db]} [_ group-id default-book-id status contents]]
    (let [default-book (get contents default-book-id)]
      {:db (assoc-in db [::data ::books-loading?] false)
       :fx [[:dispatch [::entities/merge-entity-db status contents]]
            (when default-book [:dispatch [::select-book-id group-id default-book]])]})))

(rf/reg-event-fx ::fetch-group-selections-for-book
  (fn [_ [_ group-id book]]
    {:dispatch [:comm/command-monitored {:command    :model-codes/group-selections-for-book
                                         :method     :get
                                         :data       {:group-id group-id
                                                      :book-id  (:model-code-item/id book)}
                                         :on-success [::entities/merge-entity-db]}]}))

(rf/reg-event-fx ::select-book-id
  (fn [{:keys [db]} [_ group-id book]]
    {:db (assoc-in db [::data ::selected-book-id] (:db/id book))
     :fx [[:dispatch [::clear-search-input]]
          [:dispatch [::clear-filtering]]
          [:dispatch [::fetch-group-selections-for-book group-id book]]]}))

(rf/reg-event-fx ::set-search-text
  (fn [{:keys [db]} [_ group-id book-id text]]
    (merge {:db (assoc-in db [::data ::search-text] text)}
           (when-not (str/blank? text)
             {:dispatch-debounce [{:id      ::do-search
                                   :timeout 500
                                   :action  :dispatch
                                   :event   [:comm/command-monitored {:command    :model-codes/search-for-item-ids
                                                                      :method     :get
                                                                      :data       {:group-id    group-id
                                                                                   :book-id     book-id
                                                                                   :search-term (str/trim text)}
                                                                      :on-success [::search.success]}]}]}))))

(rf/reg-event-db ::search.success
  (fn [db [_ _ found-ids]]
    (-> db
        (assoc-in [::data ::expanded-rows] found-ids)
        (assoc-in [::data ::filtered-rows] found-ids))))

(rf/reg-event-db ::clear-filtering
  (fn [db]
    (-> db
        (p/dissoc-in [::data ::expanded-rows])
        (p/dissoc-in [::data ::filtered-rows]))))

(rf/reg-event-db ::clear-search-input
  (fn [db _]
    (p/dissoc-in db [::data ::search-text])))

(rf/reg-sub ::data :-> ::data)

(rf/reg-sub ::books-loading?
  :<- [::data]
  :-> ::books-loading?)

(rf/reg-sub ::books
  :<- [::entities/context]
  (fn [ctx [_ group-id]]
    (entities/find-entities ctx {:model-code-item/group group-id
                                 :entity/type           :entity.type/model-code-book})))

(rf/reg-sub ::selected-book-id
  :<- [::data]
  :-> ::selected-book-id)

(rf/reg-sub ::selected-book
  :<- [::entities/context]
  :<- [::selected-book-id]
  (fn [[ctx book-id]]
    (entities/find-entity ctx book-id)))

(rf/reg-sub ::selected-book-sections
  :<- [::selected-book]
  :-> model-codes-model/item-sections)

(defn- child-items-signal [[_ item-id]]
  (rf/subscribe [::entities/entities {:entity/parent item-id}]))

(rf/reg-sub ::item-children
  child-items-signal
  (fn [items [_ item-id]]
    (when item-id
      (->> items
           (sort-by :ordered/order)))))

(rf/reg-sub ::has-children-with-sections?
  child-items-signal
  (fn [children _]
    (->> children
         (filter model-codes-model/can-have-sections?)
         seq
         some?)))

(rf/reg-sub ::has-children?
  child-items-signal
  (fn [children _]
    (->> children seq some?)))

(rf/reg-event-db ::toggle-row-expansion
  (fn [db [_ id]]
    (let [expanded? (-> db ::data ::expanded-rows (contains? id))]
      (update-in db [::data ::expanded-rows] (comp set (if expanded? disj conj)) id))))

(rf/reg-sub ::expanded-rows
  :<- [::data]
  (fn [data _]
    (::expanded-rows data #{})))

(rf/reg-sub ::filtered-rows
  :<- [::data]
  (fn [data _]
    (::filtered-rows data #{})))

(rf/reg-sub ::search-text
  :<- [::data]
  :-> ::search-text)

(defn model-code-search [tr group-id books & [{:keys [book-select-evt]}]]
  (let [selected-book (<sub [::selected-book])]
    [:div.flex.flex-wrap.bg-ui-light-gray.p-5.mb-5.gap-4.rounded
     [:div.flex-1 {:style {:min-width "300px"}}
      [ui-select/select {:id           "mc-book"
                         :class-name   "full-width-select"
                         :light        true
                         :allow-empty? false
                         :select-text  (tr [:model-codes/select-book])
                         :values       books
                         :val->str     :model-code-item/title
                         :value        selected-book
                         :on-change    (fn [book]
                                         (>evt [::select-book-id group-id book])
                                         (when book-select-evt
                                           (>evt book-select-evt)))}]]
     [:div.flex-1 {:style {:min-width "300px"}}
      [ui/search {:id                      "book-content-search"
                  :label-text              (tr [:dashboard-search/search])
                  :close-button-label-text (tr [:search/empty])
                  :disabled                (not selected-book)
                  :light                   true
                  :size                    :medium
                  :placeholder             (tr [:model-codes/search-placeholder])
                  :value                   (<sub [::search-text])
                  :on-change               #(rf/dispatch-sync [::set-search-text
                                                               group-id
                                                               (:model-code-item/id selected-book)
                                                               (futil/tval %)])
                  :clear-fn                #(>evt [::clear-search-input])
                  :search-in-progress?     (<sub [:comm/command-processing? :model-codes/search-for-item-ids])
                  :loading-text            (tr [:search/loading])}]]]))
